import { FolderOpenOutlined } from '@mui/icons-material';
import { Box, Stack, ThemeProvider, useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import TicketForm from '../../tickets/components/ticketForm';
import TicketTable from '../../tickets/components/ticketTable';
import ViewTicket from '../../tickets/components/viewTicket';
import { BelowTableAddButton } from '../components/belowTableAddButton';
import DocumentCardView from '../components/documentCard';
import DocumentDescriptionModal from '../components/documentDescriptionModal';
import DocumentTableView from '../components/documentTable';
import DocumentToolbar, { type FilterMode, type ViewMode } from '../components/documentToolbar';
import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import DocumentPreview from 'components/v2/common/documentPreview';
import Result from 'components/v2/common/Result';
import AddDocumentPopover from 'views/Dashboards-B2B/components/BACKOFFICEFLOW/CLIENTVIEW/components/Documents/components/AddDocumentPopover';

import api, { AppDocument } from 'api';
import { ChatReference } from 'api/rest/chat_new';
import { ACTIONS } from 'constant';
import { TicketStatus } from 'data/constants/constants';
import { DocStatus } from 'data/constants/DocStatus';
import {
    filterTicketsBySearch,
    filterTicketsByStatus,
    isTicketsFilterMode,
} from 'helpers/documentTickets';
import { useInvalidateApplicationRetainers } from 'hooks/useApplicationRetainers';
import useModalObj from 'hooks/useModalObj';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { updateDoc } from 'redux/application/document/document.middleware';
import { deleteDocs, loadDocs } from 'redux/application/documents/documents-middleware';
import { updateDocInList } from 'redux/application/documents/documents-slice';
import { AppDispatch, RootState } from 'redux/store';
import appThemeV2 from 'theme/themeV2';
import { TicketInterface } from 'types';
import { downloadBlob } from 'utils/file';
import { getExplanationUrl } from 'utils/v2/document';
import { getErrorMessage } from 'utils/v2/errorHelper';
import { ticketInitialValues } from 'utils/validations/ticket';

interface DocumentsModalData {
    ticket?: TicketInterface | null;
    document?: AppDocument | null;
}

const isTicket = (value: unknown): value is TicketInterface =>
    Boolean(value && typeof value === 'object' && 'document_id' in value && 'text' in value);

const isDocument = (value: unknown): value is AppDocument =>
    Boolean(
        value && typeof value === 'object' && 'application_id' in value && 'document_name' in value
    );

const ClientDocumentsInner = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const { list: docsList, isLoading } = useSelector((state: RootState) => state.documentsApp);
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);

    const resolveTemplateUrl = useCallback(
        (doc: AppDocument) => getExplanationUrl(doc, language),
        [language]
    );

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<ViewMode>('table');
    const [uploadingDocId, setUploadingDocId] = useState<number | null>(null);
    const [previewFile, setPreviewFile] = useState<{
        fileUrl: string;
        fileName: string;
        title?: string;
    } | null>(null);
    const [tickets, setTickets] = useState<TicketInterface[]>([]);
    const [filterMode, setFilterMode] = useState<FilterMode>('all');
    const [ticketStatusFilter, setTicketStatusFilter] = useState<TicketStatus | null>(null);

    const { modalObject, onResetModalObj, setModalObject } = useModalObj<DocumentsModalData>();
    const invalidateRetainers = useInvalidateApplicationRetainers();

    const handleViewModeChange = useCallback((mode: ViewMode) => {
        setViewMode(mode);
        localStorage.setItem('docViewMode', mode);
    }, []);

    useEffect(() => {
        const persistMode = localStorage.getItem('docViewMode');
        if (persistMode === 'card' || persistMode === 'table') {
            setViewMode(persistMode);
        }
    }, []);

    useEffect(() => {
        if (isMobile && viewMode === 'table') handleViewModeChange('card');
    }, [isMobile, viewMode, handleViewModeChange]);

    const reloadDocs = useCallback(() => {
        if (activeApp?.id) dispatch(loadDocs(activeApp.id.toString()));
    }, [activeApp?.id, dispatch]);

    useEffect(() => {
        reloadDocs();
    }, [reloadDocs]);

    const fetchTickets = useCallback(async () => {
        if (!activeApp?.id) return;
        try {
            const res = await api.tickets.getTicketsByApplication(activeApp.id.toString());
            setTickets((res.data as unknown as TicketInterface[]) ?? []);
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('ticketsLoadError')),
                    type: 'error',
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeApp?.id]);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    const openTicketCounts = useMemo(() => {
        const counts: Record<number, number> = {};
        tickets.forEach((ticket) => {
            if (
                ticket.status === TicketStatus.PENDING ||
                ticket.status === TicketStatus.IN_REVIEW
            ) {
                const docId = Number(ticket.document_id);
                counts[docId] = (counts[docId] || 0) + 1;
            }
        });
        return counts;
    }, [tickets]);

    const ticketStatusCounts = useMemo(() => {
        const relevantTickets = filterTicketsBySearch(tickets, search);
        const map = new Map<TicketStatus, number>();
        for (const ticket of relevantTickets) {
            map.set(ticket.status, (map.get(ticket.status) ?? 0) + 1);
        }
        return Object.values(TicketStatus).map((status) => ({
            status,
            count: map.get(status) ?? 0,
        }));
    }, [tickets, search]);

    const filteredDocs = useMemo(() => {
        let result = docsList;
        if (filterMode === 'status' && statusFilter) {
            result = result.filter((doc) => doc.status === statusFilter);
        }
        if (search.trim()) {
            const query = search.toLowerCase();
            result = result.filter(
                (doc) =>
                    doc.document_name.toLowerCase().includes(query) ||
                    doc.explanation?.toLowerCase().includes(query)
            );
        }
        return result;
    }, [docsList, filterMode, statusFilter, search]);

    const filteredTickets = useMemo(
        () => filterTicketsByStatus(filterTicketsBySearch(tickets, search), ticketStatusFilter),
        [tickets, search, ticketStatusFilter]
    );

    const handleFilterModeChange = useCallback(
        async (mode: FilterMode) => {
            setFilterMode(mode);
            if (mode === 'all') {
                setStatusFilter(null);
                setTicketStatusFilter(null);
            } else if (mode === 'status') {
                setTicketStatusFilter(null);
            } else if (mode === 'tickets') {
                setStatusFilter(null);
                setTicketStatusFilter(null);
                await fetchTickets();
            }
        },
        [fetchTickets]
    );

    const handleCloseModal = () => {
        if (modalObject.loading || modalObject.disabled) return;
        onResetModalObj();
    };

    const handleViewTicketDocument = useCallback(
        async (ticket: TicketInterface) => {
            if (!activeApp?.id) return;
            try {
                const resp = await api.documentResource.getDocumentPreviewUrl(
                    activeApp.id.toString(),
                    Number(ticket.document_id)
                );
                setPreviewFile({
                    fileUrl: resp.data.url,
                    fileName: ticket.document_name || translate('document'),
                });
            } catch (error) {
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('somethingWentWrong')),
                        type: 'error',
                    })
                );
            }
        },
        [activeApp?.id, dispatch, translate]
    );

    const handleAction = (action: ACTIONS, data?: TicketInterface | AppDocument) => {
        switch (action) {
            case ACTIONS.CREATE: {
                const document = isDocument(data) ? data : undefined;
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    title: translate('createTicket'),
                    data: {
                        document,
                        ticket: {
                            ...ticketInitialValues,
                            document_id: document?.id,
                        } as TicketInterface,
                    },
                    disabled: false,
                    loading: false,
                }));
                break;
            }
            case ACTIONS.EDIT:
                if (!isTicket(data)) return;
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    title: translate('editTicket'),
                    data: { ...prev.data, ticket: data },
                    disabled: false,
                    loading: false,
                }));
                break;
            case ACTIONS.VIEW: {
                const ticket = isTicket(data)
                    ? data
                    : isDocument(data)
                      ? (tickets.find(
                            (item) =>
                                Number(item.document_id) === data.id &&
                                (item.status === TicketStatus.PENDING ||
                                    item.status === TicketStatus.IN_REVIEW)
                        ) ?? tickets.find((item) => Number(item.document_id) === data.id))
                      : undefined;
                if (!ticket) return;
                setModalObject((prev) => ({
                    ...prev,
                    action: ACTIONS.VIEW,
                    title: translate('viewTicket'),
                    data: {
                        ticket,
                        document: isDocument(data) ? data : prev.data?.document,
                    },
                    disabled: false,
                    loading: false,
                }));
                break;
            }
            case ACTIONS.SELECT:
                if (!isDocument(data)) return;
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    title: translate('description'),
                    data: { document: data },
                    disabled: false,
                    loading: false,
                }));
                break;
            case ACTIONS.DELETE:
                if (!isDocument(data)) return;
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    title: translate('areYouSureDeleteDoc'),
                    content: translate('thisCannotBeUndone'),
                    data: { document: data },
                    disabled: false,
                    loading: false,
                }));
                break;
            case ACTIONS.PREVIEW_FILE:
                if (isTicket(data)) void handleViewTicketDocument(data);
                break;
            case ACTIONS.CLOSE:
                if (!isTicket(data)) return;
                setModalObject((prev) => ({
                    ...prev,
                    title: translate('closeTicket'),
                    content: translate('closeTicketConfirm').replace('{{id}}', String(data.id)),
                    data: { ...prev.data, ticket: data },
                    action,
                    disabled: false,
                    loading: false,
                }));
                break;
            case ACTIONS.CHAT: {
                if (!isTicket(data)) return;
                const references: ChatReference[] = [
                    { type: 'ticket', id: Number(data.id), name: data.text },
                ];
                navigate(`/dashboard/${id}/client-profile/chat`, {
                    state: { references },
                });
                break;
            }
            default:
                break;
        }
    };

    const handleStatusFilterChange = useCallback(
        (status: string | null) => {
            setStatusFilter(status);
            if (status === null) {
                setFilterMode((mode) => (mode === 'status' ? 'all' : mode));
                reloadDocs();
                return;
            }
            setFilterMode('status');
            setTicketStatusFilter(null);
        },
        [reloadDocs]
    );

    const handleCloseTicket = async () => {
        const ticket = modalObject.data?.ticket;
        if (!ticket) return;

        setModalObject((prev) => ({ ...prev, disabled: true, loading: true }));
        try {
            await api.tickets.editTicket({ ...ticket, status: TicketStatus.CLOSED });
            dispatch(setAlert({ message: translate('ticketClosed'), type: 'success' }));
            await fetchTickets();
            onResetModalObj();
        } catch (error) {
            setModalObject((prev) => ({ ...prev, disabled: false, loading: false }));
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        }
    };

    const handleSubmit = async (data: TicketInterface) => {
        setModalObject((prev) => ({ ...prev, disabled: true, loading: true }));
        try {
            if (data.id) {
                await api.tickets.editTicket(data);
                dispatch(setAlert({ message: translate('ticketUpdated'), type: 'success' }));
            } else {
                await api.tickets.createTicket(data);
                dispatch(setAlert({ message: translate('ticketCreated'), type: 'success' }));
            }
            await fetchTickets();
            onResetModalObj();
        } catch (error) {
            setModalObject((prev) => ({ ...prev, disabled: false, loading: false }));
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('ticketSaveError')),
                    type: 'error',
                })
            );
        }
    };

    const handleView = useCallback(
        async (doc: AppDocument) => {
            if (!activeApp?.id) return;
            try {
                const resp = await api.documentResource.getDocumentPreviewUrl(
                    activeApp.id.toString(),
                    doc.id
                );
                setPreviewFile({ fileUrl: resp.data.url, fileName: doc.document_name });
            } catch (error) {
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('somethingWentWrong')),
                        type: 'error',
                    })
                );
            }
        },
        [activeApp?.id, dispatch, translate]
    );

    const handleUpload = useCallback(
        async (doc: AppDocument, file: File) => {
            try {
                setUploadingDocId(doc.id);
                const formData = new FormData();
                formData.append('document', file);
                await api.documentResource.post(doc.id, formData);
                reloadDocs();
                dispatch(setAlert({ message: translate('documentUploaded'), type: 'success' }));
            } catch (error) {
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('somethingWentWrong')),
                        type: 'error',
                    })
                );
            } finally {
                setUploadingDocId(null);
            }
        },
        [dispatch, reloadDocs, translate]
    );

    const handleDownload = useCallback(
        async (doc: AppDocument) => {
            if (!activeApp?.id) return;
            try {
                const resp = await api.documentResource.getBlobDocument(activeApp.id, doc.id);
                downloadBlob(resp.data, `${doc.document_name}.pdf`);
            } catch (error) {
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('somethingWentWrong')),
                        type: 'error',
                    })
                );
            }
        },
        [activeApp?.id, dispatch, translate]
    );

    const handleConfirmDelete = async () => {
        const doc = modalObject.data?.document;
        if (!doc) return;

        setModalObject((prev) => ({ ...prev, loading: true, disabled: true }));
        try {
            await dispatch(deleteDocs([doc]));
            reloadDocs();
            dispatch(setAlert({ message: translate('documentDeleted'), type: 'success' }));
            onResetModalObj();
        } catch (error) {
            setModalObject((prev) => ({ ...prev, loading: false, disabled: false }));
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        }
    };

    const handleChangeStatus = useCallback(
        async (doc: AppDocument, newStatus: DocStatus) => {
            try {
                const res = await dispatch(
                    updateDoc({
                        appId: doc.application_id.toString(),
                        docId: doc.id.toString(),
                        payload: { document_name: doc.document_name, status: newStatus },
                    })
                );
                const updated = (res.payload as { data: AppDocument }).data;
                dispatch(updateDocInList(updated));
                // TWO_STAGE generates the visa retainer once every document is preapproved
                invalidateRetainers(doc.application_id);
            } catch (error) {
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('somethingWentWrong')),
                        type: 'error',
                    })
                );
            }
        },
        [dispatch, invalidateRetainers, translate]
    );

    const handleTicketStatusChange = useCallback(
        async (ticket: TicketInterface, newStatus: TicketStatus) => {
            try {
                await api.tickets.editTicket({
                    id: ticket.id,
                    status: newStatus,
                } as TicketInterface);
                dispatch(setAlert({ message: translate('ticketStatusUpdated'), type: 'success' }));
                await fetchTickets();
            } catch (error) {
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('somethingWentWrong')),
                        type: 'error',
                    })
                );
            }
        },
        [dispatch, fetchTickets, translate]
    );

    const handlePreviewDocumentFile = useCallback(
        async (doc: AppDocument, previewType: 'sample' | 'template') => {
            if (previewType === 'template' && !doc.template_uri) {
                const url = resolveTemplateUrl(doc);
                if (url) {
                    window.open(url, '_blank', 'noopener,noreferrer');
                    return;
                }
            }
            try {
                const response =
                    previewType === 'template'
                        ? await api.application.getDocumentTemplateUrl(
                              String(doc.application_id),
                              doc.id
                          )
                        : await api.application.getDocumentSampleUrl(
                              String(doc.application_id),
                              doc.id
                          );

                setPreviewFile({
                    fileUrl: response.data.url,
                    fileName: `${doc.document_name} ${
                        previewType === 'template'
                            ? translate('documentsTemplate')
                            : translate('documentsSample')
                    }`,
                    title: translate(
                        previewType === 'template' ? 'previewTemplate' : 'previewSample'
                    ),
                });
            } catch (error) {
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('somethingWentWrong')),
                        type: 'error',
                    })
                );
            }
        },
        [dispatch, resolveTemplateUrl, translate]
    );

    const viewProps = {
        documents: filteredDocs,
        uploadingDocId,
        onView: handleView,
        onUpload: handleUpload,
        onDownload: handleDownload,
        onDelete: (doc: AppDocument) => handleAction(ACTIONS.DELETE, doc),
        onChangeStatus: handleChangeStatus,
        onPreviewDocumentFile: handlePreviewDocumentFile,
        getExplanationUrl: resolveTemplateUrl,
        openTicketCounts,
        onViewTickets: (doc: AppDocument) => handleAction(ACTIONS.VIEW, doc),
        onChat: (doc: AppDocument) => {
            const references: ChatReference[] = [
                { type: 'document', id: doc.id, name: doc.document_name },
            ];
            navigate(`/dashboard/${id}/client-profile/chat`, {
                state: { references },
            });
        },
        onShowDescription: (doc: AppDocument) => handleAction(ACTIONS.SELECT, doc),
    };

    const isFormOpen = modalObject.action === ACTIONS.CREATE || modalObject.action === ACTIONS.EDIT;
    const isViewTicketOpen =
        Boolean(modalObject.data?.ticket) && modalObject.action === ACTIONS.VIEW;

    return (
        <>
            <Box sx={{ width: '100%', px: { xs: 2, md: 0 } }}>
                <Stack spacing={2} sx={{ alignItems: 'center' }}>
                    <Box sx={{ width: '100%' }}>
                        <Stack spacing={2}>
                            <DocumentToolbar
                                search={search}
                                onSearchChange={setSearch}
                                filterMode={filterMode}
                                statusFilter={statusFilter}
                                onStatusFilterChange={handleStatusFilterChange}
                                ticketStatusFilter={ticketStatusFilter}
                                onTicketStatusFilterChange={setTicketStatusFilter}
                                onFilterModeChange={handleFilterModeChange}
                                viewMode={viewMode}
                                onViewModeChange={handleViewModeChange}
                                ticketStatusCounts={ticketStatusCounts}
                            />
                            {isTicketsFilterMode(filterMode) ? (
                                <TicketTable
                                    tickets={filteredTickets}
                                    handleAction={(action, ticket) => handleAction(action, ticket)}
                                    onChangeStatus={handleTicketStatusChange}
                                />
                            ) : isLoading || docsList.length > 0 ? (
                                <>
                                    {viewMode === 'card' ? (
                                        <DocumentCardView {...viewProps} />
                                    ) : null}
                                    {viewMode === 'table' ? (
                                        <DocumentTableView
                                            {...viewProps}
                                            loading={isLoading}
                                            statusFilter={statusFilter}
                                            onStatusFilterChange={handleStatusFilterChange}
                                            onCreateTicket={(doc) =>
                                                handleAction(ACTIONS.CREATE, doc)
                                            }
                                        />
                                    ) : null}
                                </>
                            ) : (
                                <Result
                                    icon={<FolderOpenOutlined sx={{ fontSize: 32 }} />}
                                    title={translate('noDocumentsYet')}
                                    description={translate('noDocumentsYetDescription')}
                                />
                            )}
                        </Stack>
                    </Box>
                    {isTicketsFilterMode(filterMode) ? (
                        <BelowTableAddButton
                            label={translate('createTicket')}
                            onClick={() => handleAction(ACTIONS.CREATE)}
                        />
                    ) : (
                        <AddDocumentPopover
                            mobile={isMobile}
                            renderTrigger={(onClick) => (
                                <BelowTableAddButton
                                    label={translate('addDocuments')}
                                    onClick={onClick}
                                />
                            )}
                        />
                    )}
                </Stack>
            </Box>

            {previewFile ? (
                <DocumentPreview
                    open
                    mobile={isMobile}
                    onClose={() => setPreviewFile(null)}
                    title={previewFile.title ?? translate('documentPreview')}
                    fileUrl={previewFile.fileUrl}
                    fileName={previewFile.fileName}
                />
            ) : null}

            {isFormOpen ? (
                <TicketForm
                    isEdit={modalObject.action === ACTIONS.EDIT}
                    documents={docsList}
                    values={modalObject.data?.ticket as TicketInterface}
                    disabled={modalObject.disabled || modalObject.loading}
                    onClose={handleCloseModal}
                    onSubmit={handleSubmit}
                    existingTickets={tickets}
                    handleAction={(action, ticket) =>
                        handleAction(action, ticket as TicketInterface)
                    }
                    onEditExistingTicket={(value) => handleAction(ACTIONS.EDIT, value)}
                />
            ) : null}

            {isViewTicketOpen && modalObject.data?.ticket ? (
                <ViewTicket
                    ticket={modalObject.data.ticket}
                    onClose={handleCloseModal}
                    handleAction={handleAction}
                />
            ) : null}

            {modalObject.action === ACTIONS.CLOSE ? (
                <ConfirmationDialog
                    title={modalObject.title}
                    content={modalObject.content}
                    onClose={handleCloseModal}
                    onConfirm={handleCloseTicket}
                    loading={modalObject.loading || modalObject.disabled}
                />
            ) : null}

            {modalObject.action === ACTIONS.DELETE ? (
                <ConfirmationDialog
                    title={modalObject.title}
                    content={modalObject.content}
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDelete}
                    loading={modalObject.loading || modalObject.disabled}
                />
            ) : null}

            {modalObject.action === ACTIONS.SELECT && modalObject.data?.document ? (
                <DocumentDescriptionModal
                    document={modalObject.data.document}
                    onClose={handleCloseModal}
                />
            ) : null}
        </>
    );
};

const ClientDocumentsV2 = () => (
    <ThemeProvider theme={appThemeV2}>
        <ClientDocumentsInner />
    </ThemeProvider>
);

export default ClientDocumentsV2;
